"use client";

import { type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { HeartIcon, ToteIcon } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
} from "@/components/ui/drawer";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "@/lib/utils";
import DepositNotice from "@/components/product-detail/DepositNotice";
import type { DepositMethod } from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";

interface MobileBookingDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  cartItemCount: number;
  isAddingToCart: boolean;
  hasMissingRequiredOptions?: boolean;
  isChatLoading: boolean;
  onCheckout: () => void;
  onAddToCart: () => void;
  onChatClick?: () => void;
  /** 보증금 안내 (설정된 방식 없으면 미표시) */
  depositMethods?: DepositMethod[];
  depositAmount?: number | null;
  sourceCurrency: SupportedCurrency;
  /** 하단 바 왼쪽 찜 아이콘 */
  isWishlisted?: boolean;
  isWishlistLoading?: boolean;
  onWishlistClick?: () => void;
  /** 하단 바 바로 위 우측에 쌓는 원형 버튼 묶음(지도·공유·맨 위로) */
  floatingActions?: ReactNode;
}

export default function MobileBookingDrawer({
  open,
  onOpenChange,
  children,
  cartItemCount,
  isAddingToCart,
  hasMissingRequiredOptions = false,
  isChatLoading,
  onCheckout,
  onAddToCart,
  onChatClick,
  depositMethods,
  depositAmount,
  sourceCurrency,
  isWishlisted = false,
  isWishlistLoading = false,
  onWishlistClick,
  floatingActions,
}: MobileBookingDrawerProps) {
  const t = useTranslations("product.booking");

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50">
      <Drawer open={open} onOpenChange={onOpenChange}>
        {/* 원형 버튼 묶음 — 바 바로 위(bottom-full)라 바 높이가 바뀌어도 가려지지 않는다.
            드로어가 열리면 바와 함께 숨긴다. */}
        {floatingActions && (
          <div
            className={cn(
              "absolute bottom-full right-3 mb-3 transition-opacity duration-200",
              open && "opacity-0 pointer-events-none",
            )}
          >
            {floatingActions}
          </div>
        )}

        {/* Bottom Fixed Button Bar — 경계선·그림자 대신 위로 흐려지는 흰 페이드 */}
        <div
          className={cn(
            "relative bg-white px-4 pt-2.5 transition-opacity duration-200",
            "before:pointer-events-none before:absolute before:inset-x-0 before:bottom-full before:h-7 before:bg-linear-to-b before:from-white/0 before:to-white",
            "pb-[max(var(--loc-safe-area-inset-bottom),12px)]!",
            open && "opacity-0 pointer-events-none",
          )}
        >
          {/* 보증금 안내 — 접힌 하단 바에서도 버튼 위에 표시 (확정 목업) */}
          <DepositNotice
            methods={depositMethods}
            depositAmount={depositAmount}
            sourceCurrency={sourceCurrency}
            className="mb-2"
          />
          {/* 순서: 찜 · 장바구니(아이콘) · 문의하기 · 구매하기 — 주 행동은 엄지가 닿는 오른쪽 끝 */}
          <div className="flex items-center gap-2">
            {onWishlistClick && (
              <button
                type="button"
                onClick={onWishlistClick}
                disabled={isWishlistLoading}
                aria-label={t("wishlist")}
                aria-pressed={isWishlisted}
                className={cn(
                  "flex h-11 w-9 shrink-0 items-center justify-center rounded-xl transition-transform active:scale-95 active:bg-gray-100",
                  isWishlisted ? "text-red-500" : "text-gray-600",
                )}
              >
                <HeartIcon
                  weight={isWishlisted ? "fill" : "regular"}
                  className="h-5 w-5"
                />
              </button>
            )}
            <DrawerTrigger asChild>
              <button
                type="button"
                aria-label={t("addToCart")}
                className="relative flex h-11 w-9 shrink-0 items-center justify-center rounded-xl text-gray-600 transition-transform active:scale-95 active:bg-gray-100"
              >
                <ToteIcon className="h-5 w-5" />
                {cartItemCount > 0 && (
                  <span className="absolute top-1 -right-0.5 min-w-4 rounded-full bg-orange-500 px-1 text-[10px] leading-4 font-semibold text-white tabular-nums">
                    {cartItemCount}
                  </span>
                )}
              </button>
            </DrawerTrigger>
            <button
              type="button"
              onClick={onChatClick}
              disabled={isChatLoading}
              className="flex h-11 flex-1 items-center justify-center rounded-xl bg-orange-50 text-base font-semibold text-orange-700 ring-1 ring-orange-200 ring-inset transition-transform active:scale-[0.97] active:bg-orange-100 disabled:opacity-70"
            >
              {isChatLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t("inquire")
              )}
            </button>
            <DrawerTrigger asChild>
              <button
                type="button"
                className="flex h-11 flex-1 items-center justify-center rounded-xl bg-orange-500 text-base font-semibold text-white ring-1 ring-white/30 ring-inset transition-transform active:scale-[0.97] active:bg-orange-600"
              >
                {t("purchase")}
              </button>
            </DrawerTrigger>
          </div>
        </div>

        {/* Drawer Content */}
        <DrawerContent className="h-[70vh]">
          <VisuallyHidden>
            <DrawerTitle>{t("reserve")}</DrawerTitle>
          </VisuallyHidden>
          <div className="flex flex-col h-full overflow-hidden">
            {/* Scrollable Booking Content */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">{children}</div>

            {/* Fixed Bottom CTA — 총액은 선택 내역 박스(CartItemsList)에 표시 */}
            <div className="shrink-0 bg-white border-t shadow-lg px-3 pt-3 pb-3">
              {hasMissingRequiredOptions && (
                <p className="text-xs text-red-600 text-center pb-2">
                  {t("missingRequiredOptions") || "필수 옵션을 선택해주세요"}
                </p>
              )}
              {/* 보증금 안내 — 펼친 드로어 CTA에서도 버튼 바로 위에 표시 */}
              <DepositNotice
                methods={depositMethods}
                depositAmount={depositAmount}
                sourceCurrency={sourceCurrency}
                className="pb-2"
              />
              <div className="flex gap-2 items-center">
                <Button
                  variant="outline"
                  className="flex-1 h-12! rounded-xl border-orange-300 hover:bg-orange-50 font-medium text-base"
                  disabled={cartItemCount === 0 || isAddingToCart}
                  onClick={onAddToCart}
                >
                  {isAddingToCart ? (
                    <Loader2 className="h-5! w-5! animate-spin mr-2" />
                  ) : (
                    <ToteIcon className="h-5! w-5!" />
                  )}
                  {t("addToCart")}
                </Button>
                <Button
                  className="flex-1 h-12! bg-orange-500 hover:bg-orange-600 text-white text-base font-medium rounded-xl"
                  disabled={cartItemCount === 0}
                  onClick={onCheckout}
                >
                  {t("purchase")}
                </Button>
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
